// Generalized from NotificationEmptyStateAtom — promoted to a platform-wide empty-state
// primitive reusable across notification feeds, search results, and sphere selectors.
// NotificationEmptyStateAtom is notification-domain-specific; this atom is domain-agnostic.
// See spec Reuse Audit (specs/sphere-selector-and-subscriptions.md).
import React from 'react';
import GlyphAtom from '../Glyph/GlyphAtom';
import './EmptyStateAtom.scss';

export interface EmptyStateAtomProps {
  /** A PrimeIcon name without the `pi ` prefix (`pi-inbox`), or an Iconify name (`lucide:inbox`). */
  icon?: string;
  title: string;
  /** Why it is empty. Rich content is allowed — a term the reader acts on can be emphasised. */
  description?: React.ReactNode;
  /** How to proceed: the actions that fill this state, rendered beneath the description. */
  children?: React.ReactNode;
  className?: string;
  /** Test/automation hook on the root — an empty state is a state a test needs to assert. */
  'data-testid'?: string;
}

const EmptyStateAtom: React.FC<EmptyStateAtomProps> = ({
  icon = 'pi-inbox',
  title,
  description,
  className = '',
  'data-testid': testId,
  children,
}) => {
  return (
    <div className={['empty-state', className].filter(Boolean).join(' ')} data-testid={testId}>
      {icon.includes(':') ? (
        <GlyphAtom glyph={icon} className="empty-state__icon" aria-hidden />
      ) : (
        <i className={`pi ${icon} empty-state__icon`}></i>
      )}
      <p className="empty-state__title">{title}</p>
      {description && <p className="empty-state__body">{description}</p>}
      {children && <div className="empty-state__actions">{children}</div>}
    </div>
  );
};

export default EmptyStateAtom;
